import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Slider,
  TextField,
  Typography,
} from '@mui/material';
import { getHealthConfig, previewOrgHealth, resetHealthConfig, updateHealthConfig } from '../../api/dashboard';
import type {
  HealthConfig,
  HealthConfigState,
  HealthTargets,
  HealthTier,
  HealthWeights,
  OrgHealthScore,
} from '../../types';
import {
  HEALTH_WINDOW_DAYS,
  MAX_DEPTH_TARGET,
  TIER_ORDER,
  apiErrorMessage,
  healthConfigProblems,
  sameHealthConfig,
  tierChanges,
  tierCounts,
  weightSum,
  weightsBalanced,
} from '../../utils/healthScore';
import { HEALTH_TIER_STYLES } from './healthTierStyles';

/**
 * Editor for the health score's weights, pillar targets and tier cutoffs (PEL-2852).
 *
 * The config is a single company-wide definition, not a per-user view preference — the
 * API, the Organizations table and the MCP tools all score with whatever is saved here.
 * That is why this writes to the server rather than to local storage.
 */

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called after a successful save/reset so the caller can refetch scores. */
  onSaved: () => void;
  /** The scores the table is showing now, to compare the preview against; null until they load. */
  currentHealth: OrgHealthScore[] | null;
  excludedOrgIds: string[];
  excludedUserIds: string[];
}

const WEIGHT_ROWS: { key: keyof HealthWeights; label: string; help: string }[] = [
  { key: 'activation', label: 'Activation', help: 'Share of pro seats active in the window' },
  { key: 'habit', label: 'Habit', help: 'Average active days per active user' },
  { key: 'depth', label: 'Depth', help: 'AI requests per active user' },
  { key: 'reliability', label: 'Reliability', help: 'Inverse AI request failure rate' },
];

interface TargetRow {
  key: keyof HealthTargets;
  label: string;
  /** The formula, split around the editable divisor. */
  before: string;
  after?: string;
  /** What the value means, in words. */
  meaning: (value: number) => string;
  min: number;
  max: number;
  step: number;
  inputMax: number;
  decimals: number;
}

const TARGET_ROWS: TargetRow[] = [
  {
    key: 'activationSeatRatio',
    label: 'Activation',
    before: '(active users ÷ pro seats) ÷',
    meaning: (v) => `${formatPct(v)} of pro seats active scores 100`,
    min: 0.05,
    max: 1,
    step: 0.05,
    inputMax: 1,
    decimals: 2,
  },
  {
    key: 'habitActiveDays',
    label: 'Habit',
    before: 'avg active days per user ÷',
    meaning: (v) => `${v} of ${HEALTH_WINDOW_DAYS} days scores 100`,
    min: 1,
    max: HEALTH_WINDOW_DAYS,
    step: 1,
    inputMax: HEALTH_WINDOW_DAYS,
    decimals: 1,
  },
  {
    key: 'depthRequestsPerUser',
    label: 'Depth',
    before: 'AI requests per active user ÷',
    meaning: (v) => `${v} requests per active user scores 100`,
    min: 10,
    max: 500,
    step: 10,
    inputMax: MAX_DEPTH_TARGET,
    decimals: 0,
  },
  {
    key: 'reliabilityMaxFailureRate',
    label: 'Reliability',
    before: '1 − (failure rate ÷',
    after: ')',
    meaning: (v) => `a ${formatPct(v)} failure rate scores 0`,
    min: 0.01,
    max: 0.25,
    step: 0.01,
    inputMax: 1,
    decimals: 3,
  },
];

function formatPct(share: number): string {
  return `${Number((share * 100).toFixed(1))}%`;
}

function roundTo(value: number, decimals: number): number {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

/**
 * A number input that keeps what the user is typing ("0.", "0.0") instead of snapping it
 * to the parsed number on every keystroke, and re-syncs when the value changes elsewhere
 * (e.g. its slider).
 */
function NumberField({
  value,
  onChange,
  min,
  max,
  step,
  width = 64,
}: {
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  step: number;
  width?: number;
}) {
  const [text, setText] = useState(String(value));
  const focused = useRef(false);

  useEffect(() => {
    if (!focused.current) setText(String(value));
  }, [value]);

  return (
    <TextField
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        const parsed = Number(e.target.value);
        if (e.target.value.trim() !== '' && Number.isFinite(parsed)) onChange(parsed);
      }}
      onFocus={() => {
        focused.current = true;
      }}
      onBlur={() => {
        focused.current = false;
        setText(String(value));
      }}
      type="number"
      size="small"
      inputProps={{ min, max, step, style: { width } }}
    />
  );
}

function Section({ title, caption, children }: { title: string; caption: string; children: ReactNode }) {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography variant="subtitle2" fontWeight={700}>
        {title}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        {caption}
      </Typography>
      {children}
    </Box>
  );
}

function stripState(state: HealthConfigState): HealthConfig {
  return { weights: { ...state.weights }, targets: { ...state.targets }, tiers: { ...state.tiers } };
}

function TierCounts({ counts, compareTo }: { counts: Record<HealthTier, number>; compareTo?: Record<HealthTier, number> }) {
  return (
    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
      {TIER_ORDER.map((tier) => {
        const style = HEALTH_TIER_STYLES[tier];
        const delta = compareTo ? counts[tier] - compareTo[tier] : 0;
        return (
          <Chip
            key={tier}
            size="small"
            label={`${style.label} ${counts[tier]}${delta ? ` (${delta > 0 ? '+' : ''}${delta})` : ''}`}
            sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600, color: style.color, bgcolor: style.bg }}
          />
        );
      })}
    </Box>
  );
}

export default function HealthSettingsDialog({
  open,
  onClose,
  onSaved,
  currentHealth,
  excludedOrgIds,
  excludedUserIds,
}: Props) {
  const [draft, setDraft] = useState<HealthConfig | null>(null);
  const [saved, setSaved] = useState<HealthConfigState | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<OrgHealthScore[] | null>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(false);
  const previewRequest = useRef(0);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const state = await getHealthConfig();
      setSaved(state);
      setDraft(stripState(state));
    } catch (err) {
      console.error('Failed to load health settings:', err);
      setError(`Couldn't load the settings: ${apiErrorMessage(err)}`);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Re-read on every open: another CS rep may have retuned them since this tab loaded.
  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const problems = useMemo(() => (draft ? healthConfigProblems(draft) : []), [draft]);
  const isDirty = useMemo(() => Boolean(draft && saved && !sameHealthConfig(draft, stripState(saved))), [draft, saved]);

  // Preview the draft against every org, debounced so dragging a slider doesn't fire a
  // request per pixel. Stale responses are dropped by request number.
  useEffect(() => {
    const request = ++previewRequest.current;
    setPreview(null);
    setPreviewFailed(false);
    if (!open || !draft || !isDirty || problems.length > 0) {
      setIsPreviewing(false);
      return;
    }
    setIsPreviewing(true);
    const timer = window.setTimeout(async () => {
      try {
        const rows = await previewOrgHealth(draft, excludedOrgIds, excludedUserIds);
        if (request === previewRequest.current) setPreview(rows);
      } catch (err) {
        console.error('Failed to preview health settings:', err);
        if (request === previewRequest.current) setPreviewFailed(true);
      } finally {
        if (request === previewRequest.current) setIsPreviewing(false);
      }
    }, 700);
    return () => window.clearTimeout(timer);
  }, [open, draft, isDirty, problems.length, excludedOrgIds, excludedUserIds]);

  const setWeight = useCallback((key: keyof HealthWeights, value: number) => {
    // Clamp here rather than trusting the input: a pasted "5" would otherwise sit in state
    // and only fail at the server.
    const next = Number.isFinite(value) ? Math.min(Math.max(value, 0), 1) : 0;
    setDraft((prev) => (prev ? { ...prev, weights: { ...prev.weights, [key]: roundTo(next, 2) } } : prev));
  }, []);

  const setTarget = useCallback((row: TargetRow, value: number) => {
    setDraft((prev) =>
      prev ? { ...prev, targets: { ...prev.targets, [row.key]: roundTo(value, row.decimals) } } : prev,
    );
  }, []);

  const setTiers = useCallback((watchMin: number, healthyMin: number) => {
    setDraft((prev) => (prev ? { ...prev, tiers: { watchMin, healthyMin } } : prev));
  }, []);

  /** Spread the remainder across the other three weights, keeping their relative sizes. */
  const balanceAround = useCallback((key: keyof HealthWeights) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const weights = { ...prev.weights };
      const others = WEIGHT_ROWS.map((p) => p.key).filter((k) => k !== key);
      const remaining = roundTo(1 - weights[key], 2);
      const othersTotal = others.reduce((acc, k) => acc + weights[k], 0);
      if (othersTotal === 0) {
        // Nothing to scale — split what's left evenly rather than dividing by zero.
        others.forEach((k) => {
          weights[k] = roundTo(remaining / others.length, 2);
        });
      } else {
        others.forEach((k) => {
          weights[k] = roundTo((weights[k] / othersTotal) * remaining, 2);
        });
      }
      // Rounding to 2dp can leave the sum a hair off; push the drift onto the largest of
      // the others so the total lands exactly on 1.00.
      const drift = roundTo(1 - (weights[key] + others.reduce((acc, k) => acc + weights[k], 0)), 2);
      if (drift !== 0) {
        const biggest = others.reduce((a, b) => (weights[a] >= weights[b] ? a : b));
        weights[biggest] = roundTo(weights[biggest] + drift, 2);
      }
      return { ...prev, weights };
    });
  }, []);

  const handleSave = useCallback(async () => {
    if (!draft || problems.length > 0) return;
    setIsSaving(true);
    setError(null);
    try {
      const state = await updateHealthConfig(draft);
      setSaved(state);
      onSaved();
      onClose();
    } catch (err) {
      console.error('Failed to save health settings:', err);
      setError(`Couldn't save: ${apiErrorMessage(err)}`);
    } finally {
      setIsSaving(false);
    }
  }, [draft, problems.length, onClose, onSaved]);

  const handleReset = useCallback(async () => {
    setIsSaving(true);
    setError(null);
    try {
      const state = await resetHealthConfig();
      setSaved(state);
      setDraft(stripState(state));
      onSaved();
    } catch (err) {
      console.error('Failed to reset health settings:', err);
      setError(`Couldn't reset: ${apiErrorMessage(err)}`);
    } finally {
      setIsSaving(false);
    }
  }, [onSaved]);

  const currentCounts = useMemo(() => (currentHealth ? tierCounts(currentHealth) : null), [currentHealth]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" component="span" fontWeight={600}>
            Health score settings
          </Typography>
          {saved && (
            <Chip
              label={saved.isDefault ? 'Defaults' : 'Customised'}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                fontWeight: 600,
                color: saved.isDefault ? '#94a3b8' : '#8b5cf6',
                bgcolor: saved.isDefault ? 'rgba(148,163,184,0.15)' : 'rgba(139,92,246,0.15)',
              }}
            />
          )}
        </Box>
        <Typography variant="caption" color="text.secondary">
          Applies to everyone — the Organizations table, the API and the MCP tools all score with these.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              !draft && !isLoading ? (
                <Button color="inherit" size="small" onClick={load}>
                  Retry
                </Button>
              ) : undefined
            }
          >
            {error}
            {!draft && !isLoading && ' If the saved settings are broken, Reset to defaults below replaces them.'}
          </Alert>
        )}
        {isLoading || !draft ? (
          isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          )
        ) : (
          <>

            <Section
              title="Pillar weights"
              caption="How much each pillar counts toward the 0–100 score. They must total 1.00."
            >
              {WEIGHT_ROWS.map(({ key, label, help }) => (
                <Box key={key} sx={{ mb: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                    <Typography variant="body2" fontWeight={600}>
                      {label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {help}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Slider
                      value={draft.weights[key]}
                      min={0}
                      max={1}
                      step={0.01}
                      onChange={(_e, value) => setWeight(key, value as number)}
                      sx={{ flex: 1 }}
                    />
                    <NumberField value={draft.weights[key]} onChange={(v) => setWeight(key, v)} min={0} max={1} step={0.01} />
                    <Button size="small" onClick={() => balanceAround(key)} sx={{ whiteSpace: 'nowrap' }}>
                      Balance
                    </Button>
                  </Box>
                </Box>
              ))}
              <Typography
                variant="caption"
                sx={{ color: weightsBalanced(draft) ? 'success.main' : 'warning.main', fontWeight: 600 }}
              >
                {weightsBalanced(draft)
                  ? 'Weights total 1.00.'
                  : `Weights total ${weightSum(draft).toFixed(2)}. Use Balance next to the pillar you just set to spread the rest.`}
              </Typography>
            </Section>

            <Divider sx={{ mb: 2 }} />

            <Section
              title="Pillar targets"
              caption="The number each pillar is divided by. Reaching the target scores 100; each pillar is capped at 0–100."
            >
              {TARGET_ROWS.map((row) => (
                <Box key={row.key} sx={{ mb: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {row.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {row.meaning(draft.targets[row.key])}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography
                      variant="caption"
                      sx={{ fontFamily: 'monospace', color: 'text.secondary', whiteSpace: 'nowrap', width: 230, flexShrink: 0 }}
                    >
                      {row.before}
                    </Typography>
                    <NumberField
                      value={draft.targets[row.key]}
                      onChange={(v) => setTarget(row, v)}
                      min={row.min}
                      max={row.inputMax}
                      step={row.step}
                      width={72}
                    />
                    <Typography
                      variant="caption"
                      sx={{ fontFamily: 'monospace', color: 'text.secondary', width: 8, flexShrink: 0 }}
                    >
                      {row.after}
                    </Typography>
                    <Slider
                      value={Math.min(Math.max(draft.targets[row.key], row.min), row.max)}
                      min={row.min}
                      max={row.max}
                      step={row.step}
                      onChange={(_e, value) => setTarget(row, value as number)}
                      sx={{ flex: 1, ml: 1 }}
                    />
                  </Box>
                </Box>
              ))}
            </Section>

            <Divider sx={{ mb: 2 }} />

            <Section
              title="Tier cutoffs"
              caption="Drag the two handles. An org with no active pro seat is Dormant whatever its score."
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <NumberField
                  value={draft.tiers.watchMin}
                  onChange={(v) => setTiers(v, draft.tiers.healthyMin)}
                  min={0}
                  max={100}
                  step={1}
                />
                <Slider
                  value={[draft.tiers.watchMin, draft.tiers.healthyMin]}
                  min={0}
                  max={100}
                  step={1}
                  disableSwap
                  onChange={(_e, value) => {
                    const [watchMin, healthyMin] = value as number[];
                    setTiers(watchMin, healthyMin);
                  }}
                  sx={{ flex: 1 }}
                />
                <NumberField
                  value={draft.tiers.healthyMin}
                  onChange={(v) => setTiers(draft.tiers.watchMin, v)}
                  min={0}
                  max={100}
                  step={1}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                <Typography variant="caption" sx={{ color: HEALTH_TIER_STYLES.at_risk.color }}>
                  At risk &lt; {draft.tiers.watchMin}
                </Typography>
                <Typography variant="caption" sx={{ color: HEALTH_TIER_STYLES.watch.color }}>
                  Watch {draft.tiers.watchMin}–{Math.max(draft.tiers.watchMin, draft.tiers.healthyMin - 1)}
                </Typography>
                <Typography variant="caption" sx={{ color: HEALTH_TIER_STYLES.healthy.color }}>
                  Healthy ≥ {draft.tiers.healthyMin}
                </Typography>
              </Box>
            </Section>

            {problems.length > 0 ? (
              <Alert severity="warning">
                {problems.map((problem) => (
                  <Box key={problem}>{problem}</Box>
                ))}
              </Alert>
            ) : (
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  border: '1px solid rgba(148, 163, 184, 0.15)',
                  bgcolor: 'rgba(15, 23, 42, 0.4)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="caption" fontWeight={700}>
                    {isDirty ? 'Preview with these settings' : 'Orgs by tier now'}
                  </Typography>
                  {isPreviewing && <CircularProgress size={12} />}
                </Box>
                {!isDirty ? (
                  currentCounts ? (
                    <TierCounts counts={currentCounts} />
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      The table&apos;s current scores haven&apos;t loaded.
                    </Typography>
                  )
                ) : preview ? (
                  <>
                    <TierCounts counts={tierCounts(preview)} compareTo={currentCounts ?? undefined} />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      {currentHealth
                        ? `${tierChanges(currentHealth, preview)} of ${preview.length} orgs would change tier.`
                        : "The table's current scores haven't loaded, so changes can't be compared."}
                    </Typography>
                  </>
                ) : previewFailed ? (
                  <Typography variant="caption" sx={{ color: 'warning.main' }}>
                    Couldn&apos;t preview these settings. You can still save, but check the table afterwards.
                  </Typography>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    Scoring every org with these settings…
                  </Typography>
                )}
              </Box>
            )}

            {saved && !saved.isDefault && saved.updatedBy && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                Last changed by {saved.updatedBy}
                {saved.updatedAt ? ` on ${new Date(saved.updatedAt).toLocaleDateString()}` : ''}.
              </Typography>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={handleReset} disabled={isSaving || isLoading || saved?.isDefault === true} color="inherit">
          Reset to defaults
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={problems.length > 0 || !isDirty || isSaving || isLoading}
        >
          {isSaving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
